<template>
  <div class="fixed bottom-6 right-6 z-50 font-sans flex flex-col items-end gap-4">
    
    <transition name="slide-up">
      <div v-if="isOpen" class="bg-white w-80 md:w-96 h-[500px] rounded-3xl shadow-2xl flex flex-col overflow-hidden border-2 border-carpatina-verde">
        
        <!-- HEADER -->
        <div class="bg-carpatina-verde p-4 flex justify-between items-center text-white">
          <div class="flex items-center gap-2">
            <div class="bg-white/20 p-2 rounded-full">
              <Bot :size="20" />
            </div>
            <div>
              <h3 class="font-black uppercase text-sm tracking-wide">Asistent Virtual</h3>
              <p class="text-[10px] opacity-80 flex items-center gap-1">
                <span class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span> Online
              </p>
            </div>
          </div>
          <button @click="isOpen = false" class="hover:bg-white/20 p-1 rounded-lg transition">
            <X :size="20" />
          </button>
        </div>
        <!-- RESET BAR (apare doar dacă sunt mai mult de 1 mesaj) -->
        <button v-if="messages.length > 1" @click="resetChat"
                class="bg-carpatina-verde/90 text-white/70 text-[10px] font-semibold py-1 px-3 
                       hover:text-white hover:bg-carpatina-verde/70 transition flex items-center justify-center gap-1">
          <RotateCcw :size="10" />
          Conversație nouă
        </button>

        <!-- MESSAGES -->
        <div class="flex-1 bg-gray-50 p-4 overflow-y-auto space-y-4" ref="messagesContainer">
          <div v-for="(msg, index) in messages" :key="index" 
               :class="msg.isBot ? 'items-start' : 'items-end'"
               class="flex flex-col">
            
            <!-- TEXT BUBBLE -->
            <div :class="msg.isBot ? 'bg-white text-gray-700 rounded-tl-none' : 'bg-carpatina-portocaliu text-white rounded-tr-none'"
                 class="p-3 rounded-2xl shadow-sm max-w-[85%] text-sm font-medium whitespace-pre-line">
               {{ msg.text }}
            </div>

            <!-- PRODUCT RESULTS -->
            <div v-if="msg.products && msg.products.length" class="mt-2 space-y-2 w-full pl-2">
               <p class="text-[10px] font-bold text-gray-400 uppercase">Am găsit aceste sugestii:</p>
               
              <div v-for="prod in msg.products" :key="prod.id" class="relative group">
                <router-link :to="'/produs/' + prod.id" target="_blank"
                    class="block bg-white border border-carpatina-verde/20 p-2 rounded-xl text-xs font-bold text-carpatina-verde hover:bg-carpatina-verde hover:text-white transition flex justify-between items-center">
                    
                    <span class="truncate pr-2">{{ prod.nume }}</span>
                    
                    <span v-if="prod.pret" class="text-[10px] opacity-70 whitespace-nowrap">
                      {{ prod.pret }} RON
                    </span>
                    <span v-else class="text-[9px] font-black text-carpatina-portocaliu uppercase whitespace-nowrap border border-carpatina-portocaliu/30 px-1 rounded">
                      La cerere
                    </span>
                </router-link>

                <div class="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-32 bg-white p-1 rounded-xl shadow-xl border-2 border-carpatina-portocaliu z-50 animate-fade-in">
                    <img :src="'https://carpatinaprodcom.ro/api' + prod.poza" class="w-full h-24 object-cover rounded-lg bg-gray-100">
                    <div class="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-carpatina-portocaliu rotate-45"></div>
                </div>
              </div>
            </div>

            <!-- ACTION BUTTONS (WhatsApp / Telefon) -->
            <div v-if="msg.actions && msg.actions.length" class="mt-2 flex flex-wrap gap-2 pl-2">
              <a v-for="action in msg.actions" :key="action.label"
                 :href="action.url" target="_blank" rel="noopener"
                 class="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition border-2"
                 :class="action.type === 'whatsapp' 
                   ? 'bg-green-500 text-white border-green-500 hover:bg-green-600' 
                   : 'bg-white text-carpatina-verde border-carpatina-verde hover:bg-carpatina-verde hover:text-white'">
                <Phone v-if="action.type === 'phone'" :size="12" />
                <MessageCircle v-if="action.type === 'whatsapp'" :size="12" />
                {{ action.label }}
              </a>
            </div>

            <!-- SUGGESTION CHIPS -->
            <div v-if="msg.suggestions && msg.suggestions.length" class="mt-2 flex flex-wrap gap-1.5 pl-2">
              <button v-for="s in msg.suggestions" :key="s"
                @click="handleSuggestionClick(s)"
                class="text-[11px] font-semibold bg-carpatina-verde/10 text-carpatina-verde 
                       px-3 py-1.5 rounded-full hover:bg-carpatina-verde 
                       hover:text-white transition-all duration-200 hover:scale-105 active:scale-95">
                {{ s }}
              </button>
            </div>

          </div>
          
          <!-- LOADING DOTS -->
          <div v-if="isLoading" class="flex gap-1 pl-2">
            <span class="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></span>
            <span class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 0.1s"></span>
            <span class="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style="animation-delay: 0.2s"></span>
          </div>
        </div>

        <!-- INPUT -->
        <form @submit.prevent="sendMessage" class="p-3 bg-white border-t border-gray-100 flex gap-2">
          <input v-model="userInput" 
                 placeholder="Scrie aici (ex: gresie, contact)..." 
                 class="flex-1 bg-gray-100 rounded-xl px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-carpatina-verde/50 transition" />
          <button type="submit" 
                  :disabled="!userInput.trim()"
                  class="bg-carpatina-portocaliu text-white p-2.5 rounded-xl hover:scale-105 active:scale-95 transition disabled:opacity-50">
            <Send :size="18" />
          </button>
        </form>

      </div>
    </transition>

    <!-- TOGGLE BUTTON -->
    <button @click="isOpen = !isOpen" 
            class="bg-carpatina-verde text-white p-4 rounded-full shadow-xl hover:scale-110 hover:rotate-3 transition-all duration-300 border-4 border-white flex items-center justify-center group relative">
       <span v-if="!isOpen" class="absolute -top-1 -right-1 flex h-3 w-3">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
       </span>
       <HelpCircle v-if="!isOpen" :size="28" class="group-hover:animate-wiggle" />
       <ChevronDown v-else :size="28" />
    </button>

  </div>
</template>

<script setup>
import { ref, nextTick, watch } from 'vue';
import axios from 'axios';
import { HelpCircle, X, Send, Bot, ChevronDown, Phone, MessageCircle, RotateCcw } from 'lucide-vue-next';

// ============================================
// CONFIG
// ============================================
const PHONE_NUMBER = '0247 412 068';
const WHATSAPP_NUMBER = '40768710820';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

// ============================================
// FAQ DATABASE
// Adaugă oricâte intrări vrei aici fără să atingi logica
// ============================================
const FAQ_DATABASE = [
  {
    keywords: ['contact', 'telefon', 'suna', 'apel', 'numar', 'email', 'mail'],
    answer: `Ne poți contacta oricând:\n📞 Telefon: ${PHONE_NUMBER}\n💬 WhatsApp: +40 768 710 820\n📧 Email: office@carpatinaprodcom.ro`,
    suggestions: ['Program', 'Livrare', 'Produse'],
    actions: [
      { label: 'WhatsApp', url: `${WHATSAPP_URL}?text=Bună ziua, am o întrebare.`, type: 'whatsapp' },
      { label: 'Sună acum', url: `tel:${PHONE_NUMBER.replace(/\s/g, '')}`, type: 'phone' }
    ]
  },
  {
    keywords: ['program', 'ora', 'deschis', 'inchis', 'orar', 'cand'],
    answer: 'Programul nostru:\n🕗 Luni – Vineri: 8:00 – 17:00\n🕘 Sâmbătă: 9:00 – 13:00\n❌ Duminică: Închis',
    suggestions: ['Contact', 'Livrare', 'Produse']
  },
  {
    keywords: ['livrare', 'transport', 'livrati', 'curier', 'expediati', 'trimite', 'trimiteti'],
    answer: 'Oferim livrare în tot județul și împrejurimi! 🚛\nPentru detalii despre costuri și disponibilitate, contactează-ne.',
    suggestions: ['Sună-ne', 'Produse'],
    actions: [
      { label: 'Întreabă pe WhatsApp', url: `${WHATSAPP_URL}?text=Bună ziua, aș dori informații despre livrare.`, type: 'whatsapp' },
      { label: 'Sună acum', url: `tel:${PHONE_NUMBER.replace(/\s/g, '')}`, type: 'phone' }
    ]
  },
  {
    keywords: ['plata', 'card', 'cash', 'numerar', 'rata', 'rate', 'platesc', 'bancar', 'transfer'],
    answer: 'Modalități de plată acceptate:\n💵 Cash la sediu\n🏦 Transfer bancar\nPentru comenzi mari, contactează-ne pentru detalii.',
    suggestions: ['Contact', 'Livrare']
  },
  {
    keywords: ['garantie', 'retur', 'schimb', 'defect', 'reclamatie', 'problema'],
    answer: 'Toate produsele noastre beneficiază de garanție conform legii. Pentru reclamații sau retururi, contactează-ne direct.',
    suggestions: ['Sună-ne', 'Contact'],
    actions: [
      { label: 'WhatsApp', url: `${WHATSAPP_URL}?text=Bună ziua, am o problemă cu o comandă.`, type: 'whatsapp' }
    ]
  },
  {
    keywords: ['unde', 'adresa', 'locatie', 'sediu', 'magazin', 'showroom', 'harta'],
    answer: 'Ne găsești la sediul nostru din Turnu Măgurele.\nVino să vezi produsele direct! 📍',
    suggestions: ['Program', 'Contact']
  },
  {
    keywords: ['multumesc', 'mersi', 'ms', 'merci', 'super', 'ok'],
    answer: 'Cu plăcere! Dacă mai ai nevoie de ceva, sunt aici. 😊',
    suggestions: ['Produse', 'Contact']
  },
  {
    keywords: ['salut', 'buna', 'hey', 'hello', 'servus', 'neata', 'ceau'],
    answer: 'Salutare! 👋 Cu ce te pot ajuta?',
    suggestions: ['Produse', 'Contact', 'Program', 'Livrare']
  }
];

// ============================================
// STATE
// ============================================
const STORAGE_KEY = 'carpatina_chat';
const SESSION_EXPIRY_MS = 30 * 60 * 1000; // 30 minute — după asta resetează conversația

const defaultMessages = [
  { 
    text: 'Salut! 👋 Sunt asistentul Carpatina. Cu ce te pot ajuta?', 
    isBot: true,
    suggestions: ['Produse', 'Contact', 'Program', 'Livrare']
  }
];

/**
 * Încarcă conversația din localStorage dacă nu e expirată
 */
const loadMessages = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultMessages;
    
    const { messages: savedMsgs, timestamp } = JSON.parse(saved);
    
    // Dacă au trecut 30 min, resetează (conversație nouă)
    if (Date.now() - timestamp > SESSION_EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return defaultMessages;
    }

    // Scoatem suggestions/actions din mesajele vechi (nu mai sunt relevante)
    // Păstrăm doar pe ultimul mesaj bot
    return savedMsgs.map((msg, i) => {
      if (i < savedMsgs.length - 1) {
        return { ...msg, suggestions: undefined, actions: undefined };
      }
      return msg;
    });
  } catch {
    return defaultMessages;
  }
};

const isOpen = ref(false);
const userInput = ref('');
const isLoading = ref(false);
const messagesContainer = ref(null);
const messages = ref(loadMessages());

/**
 * Salvează conversația în localStorage la fiecare schimbare
 */
watch(messages, (val) => {
  try {
    // Salvăm max ultimele 50 mesaje ca să nu umfle storage-ul
    const toSave = val.slice(-50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      messages: toSave,
      timestamp: Date.now()
    }));
  } catch {
    // localStorage plin sau dezactivat — nu crăpă nimic
  }
}, { deep: true });

// ============================================
// HELPERS
// ============================================
const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

watch(isOpen, (val) => {
  if (val) scrollToBottom();
});

/**
 * Resetează conversația (util pentru un buton "conversație nouă")
 */
const resetChat = () => {
  localStorage.removeItem(STORAGE_KEY);
  messages.value = [...defaultMessages];
};

/**
 * Normalizează textul: lowercase, fără diacritice comune
 */
const normalize = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[ăâ]/g, 'a')
    .replace(/[îï]/g, 'i')
    .replace(/[șş]/g, 's')
    .replace(/[țţ]/g, 't')
    .replace(/[ö]/g, 'o')
    .replace(/[ü]/g, 'u')
    .trim();
};

/**
 * Caută cel mai bun FAQ match pe bază de scor
 * Returnează entry-ul cu cele mai multe keyword-uri potrivite, sau null
 */
const findBestFaqMatch = (text) => {
  const normalizedText = normalize(text);
  let bestMatch = null;
  let bestScore = 0;

  for (const entry of FAQ_DATABASE) {
    let score = 0;
    for (const kw of entry.keywords) {
      if (normalizedText.includes(normalize(kw))) {
        score++;
      }
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = entry;
    }
  }

  return bestScore > 0 ? bestMatch : null;
};

// ============================================
// SUGGESTION CHIP HANDLER
// ============================================
const handleSuggestionClick = (suggestion) => {
  // Mapăm anumite chipuri la acțiuni directe
  const directActions = {
    'sună-ne': () => { window.open(`tel:${PHONE_NUMBER.replace(/\s/g, '')}`, '_self'); },
    'whatsapp': () => { window.open(`${WHATSAPP_URL}?text=Bună ziua, am o întrebare.`, '_blank'); },
  };

  const normalized = normalize(suggestion);
  if (directActions[normalized]) {
    directActions[normalized]();
    return;
  }

  // Altfel, trimite ca mesaj normal
  userInput.value = suggestion;
  sendMessage();
};

// ============================================
// SEND MESSAGE
// ============================================
const sendMessage = async () => {
  const text = userInput.value.trim();
  if (!text) return;

  // Adaugă mesajul utilizatorului
  messages.value.push({ text: text, isBot: false });
  userInput.value = '';
  isLoading.value = true;
  scrollToBottom();

  setTimeout(async () => {
    let botResponse = { text: '', isBot: true, products: [], suggestions: [], actions: [] };

    // 1) Verifică FAQ
    const faqMatch = findBestFaqMatch(text);

    if (faqMatch) {
      botResponse.text = faqMatch.answer;
      botResponse.suggestions = faqMatch.suggestions || [];
      botResponse.actions = faqMatch.actions || [];
    }
    // 2) Altfel, caută produse
    else {
      try {
        const res = await axios.get(`/chat/search?query=${encodeURIComponent(text)}`);
        
        if (res.data && res.data.length > 0) {
          const count = res.data.length;
          botResponse.text = count === 1
            ? 'Am găsit un produs care s-ar putea să te intereseze. Ține mouse-ul pe el pentru poză!'
            : `Am găsit ${count} produse care s-ar putea să te intereseze. Ține mouse-ul pe ele pentru poză!`;
          botResponse.products = res.data;
          botResponse.suggestions = ['Contact', 'Livrare'];
        } else {
          botResponse.text = 'Nu am găsit produse cu acest nume. Încearcă alt cuvânt cheie sau contactează-ne direct!';
          botResponse.suggestions = ['Gresie', 'Ciment', 'Adeziv', 'Sună-ne'];
          botResponse.actions = [
            { label: 'Întreabă pe WhatsApp', url: `${WHATSAPP_URL}?text=Bună ziua, caut: ${text}`, type: 'whatsapp' },
            { label: 'Sună acum', url: `tel:${PHONE_NUMBER.replace(/\s/g, '')}`, type: 'phone' }
          ];
        }
      } catch (err) {
        botResponse.text = 'Am întâmpinat o eroare de conexiune. Poți să ne contactezi direct:';
        botResponse.actions = [
          { label: 'WhatsApp', url: `${WHATSAPP_URL}?text=Bună ziua, am o întrebare.`, type: 'whatsapp' },
          { label: 'Sună acum', url: `tel:${PHONE_NUMBER.replace(/\s/g, '')}`, type: 'phone' }
        ];
      }
    }

    messages.value.push(botResponse);
    isLoading.value = false;
    scrollToBottom();
  }, 500);
};
</script>

<style scoped>
.slide-up-enter-active, .slide-up-leave-active { 
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); 
  transform-origin: bottom right; 
}
.slide-up-enter-from, .slide-up-leave-to { 
  opacity: 0; 
  transform: scale(0.5) translateY(20px); 
}

@keyframes wiggle { 
  0%, 100% { transform: rotate(-3deg); } 
  50% { transform: rotate(3deg); } 
}
.group-hover\:animate-wiggle:hover { 
  animation: wiggle 0.3s ease-in-out infinite; 
}
</style>